import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { calculateGuardianStability } from "../engine/guardianEngine";
import { getGuardianStatus } from "../engine/guardianStatus";
import { fetchGuardian } from "../services/api";
import { mapGuardianApiState } from "../utils/guardianDiagnostics";

const GuardianContext = createContext();

export function GuardianProvider({ children, portfolio = [], stocks = [] }) {
  const [remoteGuardianState, setRemoteGuardianState] = useState(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const marketData = useMemo(
    () => ({
      volatilityLevel: "normal"
    }),
    []
  );

  const guardianState = useMemo(() => {
    const stabilityScore = calculateGuardianStability({
      portfolio,
      stocks,
      marketData
    });

    const status = getGuardianStatus(stabilityScore);

    return {
      score: stabilityScore,
      status: status.label,
      level: status.level,
      message: status.message,
      lastUpdate: new Date().toISOString(),
      dataSource: "local_portfolio_model",
      syncStatus: "local_only",
      dataState: "degraded",
      timestampLabel: null,
      fetchError: "",
      apiData: null,
    };
  }, [portfolio, stocks, marketData]);

  useEffect(() => {
    let cancelled = false;

    const loadGuardian = async () => {
      try {
        const { data, error } = await fetchGuardian();
        const mapped = !error ? mapGuardianApiState(data) : null;

        if (!mapped) {
          throw new Error(error || "Guardian-Live-Daten konnten nicht verarbeitet werden.");
        }

        if (!cancelled) {
          setRemoteGuardianState(mapped);
          setFetchError("");
        }
      } catch (err) {
        if (!cancelled) {
          setFetchError(String(err?.message || "Guardian-Live-Daten konnten nicht geladen werden."));
        }
      } finally {
        if (!cancelled) {
          setHasAttemptedFetch(true);
        }
      }
    };

    void loadGuardian();
    const intervalId = window.setInterval(loadGuardian, 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const resolvedGuardianState = useMemo(() => {
    if (remoteGuardianState) {
      return {
        ...remoteGuardianState,
        fetchError: "",
      };
    }

    if (!hasAttemptedFetch) {
      return {
        ...guardianState,
        syncStatus: "processing",
        dataState: "processing",
        message: "Guardian lädt Live-Daten und nutzt bis dahin den lokalen Sicherheitsstand.",
      };
    }

    return {
      ...guardianState,
      syncStatus: "fallback",
      fetchError,
      message: guardianState.message,
    };
  }, [fetchError, guardianState, hasAttemptedFetch, remoteGuardianState]);

  return (
    <GuardianContext.Provider value={resolvedGuardianState}>
      {children}
    </GuardianContext.Provider>
  );
}

export function useGuardian() {
  const context = useContext(GuardianContext);
  if (!context) {
    throw new Error("useGuardian must be used within GuardianProvider");
  }
  return context;
}
